// Strat DERIVAT + FILTRAT — singurul loc care combină sursa de date (`store.ts`)
// cu filtrele globale (`filters.ts`). Secțiunile citesc DOAR de aici pentru
// tot ce e filtrabil / drill-down-abil.
//
// Principiu: fact-ul granular „comenzi" (grain = comandă) este sursa primară
// pentru profitabilitate, top clienți, mix, livrări și split export — astfel
// filtrele (perioadă / client / categorie / segment) chiar recalculează totul.
// Când `comenzi` lipsește (import legacy fără el), cădem elegant pe seturile
// agregate existente (fără filtrare pe dimensiuni pe care nu le avem).

import type {
  ComandaRow,
  CostProductieRow,
  ExportSplitRow,
  ExportTrendRow,
  IncarcareMasinaRow,
  MediaMobilaRow,
  PierdereRow,
  ProfitabilitateRow,
} from "@/types";
import {
  getComenzi,
  getCostProductie,
  getExportSplit,
  getExportTrend,
  getIncarcareMasini,
  getMediaMobila,
  getPierderi,
  getProfitabilitate,
  hasComenzi,
} from "./dataSource";
import { getFilters, type FilterState } from "./filters";

const r1 = (n: number) => Math.round(n * 10) / 10;
const r2 = (n: number) => Math.round(n * 100) / 100;

// ─── Univers de luni + fereastra activă ──────────────────────────────────────
export function allMonths(): string[] {
  const src = hasComenzi() ? getComenzi().map((c) => c.luna_key) : getCostProductie().map((c) => c.luna_key);
  return Array.from(new Set(src)).sort();
}

export interface Window {
  from: string;
  to: string;
}

function presetCount(f: FilterState): number | null {
  if (f.preset === "1") return 1;
  if (f.preset === "3") return 3;
  if (f.preset === "6") return 6;
  if (f.preset === "12") return 12;
  return null; // "all" | "custom"
}

/** Rezolvă fereastra [from,to] (luna_key) din filtre, față de lista reală de luni. */
export function resolveWindow(f: FilterState, months: string[]): Window {
  if (!months.length) return { from: "0000-00", to: "9999-99" };
  if (f.preset === "custom") {
    return {
      from: f.customFrom ?? months[0],
      to: f.customTo ?? months[months.length - 1],
    };
  }
  const n = presetCount(f);
  if (n === null) return { from: months[0], to: months[months.length - 1] }; // all
  const slice = months.slice(Math.max(0, months.length - n));
  return { from: slice[0], to: slice[slice.length - 1] };
}

export function activeWindow(): Window {
  return resolveWindow(getFilters(), allMonths());
}

export function monthsInWindow(): string[] {
  const w = activeWindow();
  return allMonths().filter((m) => m >= w.from && m <= w.to);
}

function inWindow(luna_key: string, w: Window): boolean {
  return luna_key >= w.from && luna_key <= w.to;
}

// ─── Comenzi filtrate ────────────────────────────────────────────────────────
/** Bază: doar fereastră + segment (folosită și de drill-down). */
export function comenziWindowSegment(): ComandaRow[] {
  const f = getFilters();
  const w = activeWindow();
  return getComenzi().filter(
    (c) => inWindow(c.luna_key, w) && (f.segment === "all" || c.segment === f.segment),
  );
}

/** Comenzi complet filtrate: + client + categorie + mașină. */
export function filteredComenzi(): ComandaRow[] {
  const f = getFilters();
  return comenziWindowSegment().filter(
    (c) =>
      (f.client === "all" || c.client === f.client) &&
      (f.categorie === "all" || c.categorie === f.categorie) &&
      (f.masina === "all" || c.masina === f.masina),
  );
}

// dimensiuni disponibile (pentru dropdown-urile de filtre)
export function distinctClienti(): string[] {
  return Array.from(new Set(getComenzi().map((c) => c.client))).sort();
}
export function distinctCategorii(): string[] {
  return Array.from(new Set(getComenzi().map((c) => c.categorie))).sort();
}
export function distinctMasini(): string[] {
  const din = hasComenzi()
    ? getComenzi().map((c) => c.masina)
    : getIncarcareMasini().map((m) => m.nume_masina);
  return Array.from(new Set(din)).sort();
}

// ─── Agregare generică ───────────────────────────────────────────────────────
interface Agg {
  nr_comenzi: number;
  venit_eur: number;
  cost_eur: number;
  cantitate_m2: number;
}
function emptyAgg(): Agg {
  return { nr_comenzi: 0, venit_eur: 0, cost_eur: 0, cantitate_m2: 0 };
}
function addToAgg(a: Agg, c: ComandaRow) {
  a.nr_comenzi += 1;
  a.venit_eur += c.venit_eur;
  a.cost_eur += c.cost_eur;
  a.cantitate_m2 += c.cantitate_m2;
}

// ─── Profitabilitate (per client / categorie) ───────────────────────────────
export function deriveProfitabilitate(dim: "client" | "categorie_produs"): ProfitabilitateRow[] {
  if (!hasComenzi()) {
    // fallback pe setul agregat existent (nefiltrabil pe perioadă)
    return getProfitabilitate().filter((r) => r.dimensiune === dim);
  }
  const cheie = (c: ComandaRow) => (dim === "client" ? c.client : c.categorie);
  const map = new Map<string, Agg>();
  for (const c of filteredComenzi()) {
    const k = cheie(c);
    if (!map.has(k)) map.set(k, emptyAgg());
    addToAgg(map.get(k)!, c);
  }
  return Array.from(map.entries()).map(([valoare, a]) => {
    const marja = a.venit_eur - a.cost_eur;
    return {
      dimensiune: dim,
      valoare,
      nr_comenzi: a.nr_comenzi,
      venit_eur: r2(a.venit_eur),
      cost_eur: r2(a.cost_eur),
      marja_eur: r2(marja),
      marja_pct: a.venit_eur > 0 ? r1((marja / a.venit_eur) * 100) : 0,
      prag_pret_min_pct: a.venit_eur > 0 ? r1((a.cost_eur / a.venit_eur) * 100) : 0,
    } satisfies ProfitabilitateRow;
  });
}

// ─── Top clienți (P2) ────────────────────────────────────────────────────────
export interface TopClientRow {
  client: string;
  segment: "export" | "intern" | "mixt";
  nr_comenzi: number;
  venit_eur: number;
  cost_eur: number;
  marja_eur: number;
  marja_pct: number;
  cantitate_m2: number;
  pondere_venit_pct: number;
}

export function deriveTopClienti(): TopClientRow[] {
  const map = new Map<string, Agg & { segmente: Set<string> }>();
  for (const c of filteredComenzi()) {
    if (!map.has(c.client)) map.set(c.client, { ...emptyAgg(), segmente: new Set() });
    const a = map.get(c.client)!;
    addToAgg(a, c);
    a.segmente.add(c.segment);
  }
  const total = Array.from(map.values()).reduce((s, a) => s + a.venit_eur, 0);
  return Array.from(map.entries())
    .map(([client, a]) => {
      const marja = a.venit_eur - a.cost_eur;
      const seg: TopClientRow["segment"] =
        a.segmente.size > 1 ? "mixt" : (Array.from(a.segmente)[0] as "export" | "intern");
      return {
        client,
        segment: seg,
        nr_comenzi: a.nr_comenzi,
        venit_eur: r2(a.venit_eur),
        cost_eur: r2(a.cost_eur),
        marja_eur: r2(marja),
        marja_pct: a.venit_eur > 0 ? r1((marja / a.venit_eur) * 100) : 0,
        cantitate_m2: r1(a.cantitate_m2),
        pondere_venit_pct: total > 0 ? r1((a.venit_eur / total) * 100) : 0,
      };
    })
    .sort((a, b) => b.venit_eur - a.venit_eur);
}

// ─── Mix produse & sezonalitate (P2) ─────────────────────────────────────────
export interface MixResult {
  luni: string[]; // luna_key în fereastră
  categorii: string[]; // categoriile prezente
  /** pivot: [{ luna, [categorie]: venit_eur }] pentru chart stacked */
  serie: Array<Record<string, number | string>>;
  /** total venit per categorie (pentru donut / clasament) */
  totalPerCategorie: Array<{ categorie: string; venit_eur: number; pondere_pct: number }>;
}

export function deriveMix(): MixResult {
  const rows = filteredComenzi();
  const luni = monthsInWindow();
  const categorii = Array.from(new Set(rows.map((r) => r.categorie))).sort();
  const perLunaCat = new Map<string, number>(); // `${luna}|${cat}` -> venit
  const totalCat = new Map<string, number>();
  for (const c of rows) {
    const k = `${c.luna_key}|${c.categorie}`;
    perLunaCat.set(k, (perLunaCat.get(k) ?? 0) + c.venit_eur);
    totalCat.set(c.categorie, (totalCat.get(c.categorie) ?? 0) + c.venit_eur);
  }
  const serie = luni.map((luna) => {
    const row: Record<string, number | string> = { luna };
    for (const cat of categorii) row[cat] = r2(perLunaCat.get(`${luna}|${cat}`) ?? 0);
    return row;
  });
  const totalVenit = Array.from(totalCat.values()).reduce((s, v) => s + v, 0);
  const totalPerCategorie = categorii
    .map((categorie) => ({
      categorie,
      venit_eur: r2(totalCat.get(categorie) ?? 0),
      pondere_pct: totalVenit > 0 ? r1(((totalCat.get(categorie) ?? 0) / totalVenit) * 100) : 0,
    }))
    .sort((a, b) => b.venit_eur - a.venit_eur);
  return { luni, categorii, serie, totalPerCategorie };
}

// ─── Pierderi / waste pe operații (P2) ───────────────────────────────────────
export interface WasteRow {
  operatie: string;
  intrare_m2: number;
  pierdere_m2: number;
  pierdere_pct: number;
  cost_pierdere_eur: number;
}
export interface WasteResult {
  peOperatie: WasteRow[];
  peLuna: Array<{ luna: string; pierdere_pct: number; cost_pierdere_eur: number }>;
  totalCost: number;
  totalPierdereM2: number;
  pctMediu: number;
}

function pierderiInWindow(): PierdereRow[] {
  const w = activeWindow();
  return getPierderi().filter((p) => inWindow(p.luna_key, w));
}

export function deriveWaste(): WasteResult {
  const rows = pierderiInWindow();
  const map = new Map<string, { intrare: number; pierdere: number; cost: number }>();
  for (const p of rows) {
    if (!map.has(p.operatie)) map.set(p.operatie, { intrare: 0, pierdere: 0, cost: 0 });
    const a = map.get(p.operatie)!;
    a.intrare += p.intrare_m2;
    a.pierdere += p.pierdere_m2;
    a.cost += p.cost_pierdere_eur;
  }
  const peOperatie = Array.from(map.entries())
    .map(([operatie, a]) => ({
      operatie,
      intrare_m2: r1(a.intrare),
      pierdere_m2: r1(a.pierdere),
      pierdere_pct: a.intrare > 0 ? r1((a.pierdere / a.intrare) * 100) : 0,
      cost_pierdere_eur: r2(a.cost),
    }))
    .sort((a, b) => b.cost_pierdere_eur - a.cost_pierdere_eur);

  const luniMap = new Map<string, { intrare: number; pierdere: number; cost: number }>();
  for (const p of rows) {
    if (!luniMap.has(p.luna_key)) luniMap.set(p.luna_key, { intrare: 0, pierdere: 0, cost: 0 });
    const a = luniMap.get(p.luna_key)!;
    a.intrare += p.intrare_m2;
    a.pierdere += p.pierdere_m2;
    a.cost += p.cost_pierdere_eur;
  }
  const peLuna = Array.from(luniMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([luna, a]) => ({
      luna,
      pierdere_pct: a.intrare > 0 ? r1((a.pierdere / a.intrare) * 100) : 0,
      cost_pierdere_eur: r2(a.cost),
    }));

  const totalCost = r2(peOperatie.reduce((s, r) => s + r.cost_pierdere_eur, 0));
  const totalPierdereM2 = r1(peOperatie.reduce((s, r) => s + r.pierdere_m2, 0));
  const totalIntrare = peOperatie.reduce((s, r) => s + r.intrare_m2, 0);
  const pctMediu = totalIntrare > 0 ? r1((totalPierdereM2 / totalIntrare) * 100) : 0;
  return { peOperatie, peLuna, totalCost, totalPierdereM2, pctMediu };
}

// ─── Performanță livrări (P2) ────────────────────────────────────────────────
export interface LivrariResult {
  totalComenzi: number;
  laTimp: number;
  intarziate: number;
  procentLaTimp: number;
  intarziereMedieZile: number;
  peLuna: Array<{ luna: string; procent_la_timp: number; intarziere_medie: number }>;
  topIntarziati: Array<{ client: string; nr_comenzi: number; intarziere_medie: number; procent_la_timp: number }>;
}

export function deriveLivrari(): LivrariResult {
  const rows = filteredComenzi();
  const total = rows.length;
  const laTimp = rows.filter((c) => c.intarziere_zile <= 0).length;
  const intarziate = total - laTimp;
  const sumaIntarziere = rows.reduce((s, c) => s + Math.max(0, c.intarziere_zile), 0);

  const luniMap = new Map<string, ComandaRow[]>();
  for (const c of rows) {
    if (!luniMap.has(c.luna_key)) luniMap.set(c.luna_key, []);
    luniMap.get(c.luna_key)!.push(c);
  }
  const peLuna = Array.from(luniMap.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([luna, arr]) => {
      const ok = arr.filter((c) => c.intarziere_zile <= 0).length;
      const dl = arr.reduce((s, c) => s + Math.max(0, c.intarziere_zile), 0);
      return {
        luna,
        procent_la_timp: arr.length > 0 ? r1((ok / arr.length) * 100) : 0,
        intarziere_medie: arr.length > 0 ? r1(dl / arr.length) : 0,
      };
    });

  const cliMap = new Map<string, ComandaRow[]>();
  for (const c of rows) {
    if (!cliMap.has(c.client)) cliMap.set(c.client, []);
    cliMap.get(c.client)!.push(c);
  }
  const topIntarziati = Array.from(cliMap.entries())
    .map(([client, arr]) => {
      const ok = arr.filter((c) => c.intarziere_zile <= 0).length;
      const dl = arr.reduce((s, c) => s + Math.max(0, c.intarziere_zile), 0);
      return {
        client,
        nr_comenzi: arr.length,
        intarziere_medie: r1(dl / arr.length),
        procent_la_timp: r1((ok / arr.length) * 100),
      };
    })
    .sort((a, b) => b.intarziere_medie - a.intarziere_medie);

  return {
    totalComenzi: total,
    laTimp,
    intarziate,
    procentLaTimp: total > 0 ? r1((laTimp / total) * 100) : 0,
    intarziereMedieZile: total > 0 ? r1(sumaIntarziere / total) : 0,
    peLuna,
    topIntarziati,
  };
}

// ─── Export split + trend (filtrate) ─────────────────────────────────────────
export function deriveExportSplit(): ExportSplitRow[] {
  if (!hasComenzi()) return getExportSplit();
  const rows = filteredComenzi();
  const map = new Map<"export" | "intern", Agg>();
  for (const c of rows) {
    if (!map.has(c.segment)) map.set(c.segment, emptyAgg());
    addToAgg(map.get(c.segment)!, c);
  }
  const totalVenit = Array.from(map.values()).reduce((s, a) => s + a.venit_eur, 0);
  const totalComenzi = Array.from(map.values()).reduce((s, a) => s + a.nr_comenzi, 0);
  const segmente: Array<"export" | "intern"> = ["export", "intern"];
  return segmente
    .filter((s) => map.has(s))
    .map((segment) => {
      const a = map.get(segment)!;
      const marja = a.venit_eur - a.cost_eur;
      return {
        segment,
        nr_comenzi: a.nr_comenzi,
        venit_eur: r2(a.venit_eur),
        pondere_venit_pct: totalVenit > 0 ? r1((a.venit_eur / totalVenit) * 100) : 0,
        pondere_comenzi_pct: totalComenzi > 0 ? r1((a.nr_comenzi / totalComenzi) * 100) : 0,
        marja_eur: r2(marja),
        marja_pct: a.venit_eur > 0 ? r1((marja / a.venit_eur) * 100) : 0,
      } satisfies ExportSplitRow;
    });
}

export function deriveExportTrend(): ExportTrendRow[] {
  if (!hasComenzi()) {
    const w = activeWindow();
    return getExportTrend().filter((r) => inWindow(r.luna_key, w));
  }
  const luni = monthsInWindow();
  const perLuna = new Map<string, { exp: number; tot: number }>();
  for (const c of comenziWindowSegment()) {
    if (!perLuna.has(c.luna_key)) perLuna.set(c.luna_key, { exp: 0, tot: 0 });
    const a = perLuna.get(c.luna_key)!;
    a.tot += c.venit_eur;
    if (c.segment === "export") a.exp += c.venit_eur;
  }
  return luni.map((luna_key) => {
    const a = perLuna.get(luna_key) ?? { exp: 0, tot: 0 };
    return { luna_key, export_pct_venit: a.tot > 0 ? r1((a.exp / a.tot) * 100) : 0 };
  });
}

// ─── Passthrough time-based (doar fereastră) ─────────────────────────────────
export function costInWindow(): CostProductieRow[] {
  const w = activeWindow();
  return getCostProductie().filter((c) => inWindow(c.luna_key, w));
}
export function mediaInWindow(): MediaMobilaRow[] {
  const w = activeWindow();
  return getMediaMobila().filter((c) => inWindow(c.luna_key, w));
}
export function incarcareInWindow(): IncarcareMasinaRow[] {
  const f = getFilters();
  const w = activeWindow();
  return getIncarcareMasini().filter(
    (m) => inWindow(m.luna_key, w) && (f.masina === "all" || m.nume_masina === f.masina),
  );
}

// ─── Drill-down: comenzi pe o dimensiune anume (respectă fereastra + segment) ─
export function comenziByClient(client: string): ComandaRow[] {
  return comenziWindowSegment().filter((c) => c.client === client);
}
export function comenziByCategorie(categorie: string): ComandaRow[] {
  return comenziWindowSegment().filter((c) => c.categorie === categorie);
}
export function comenziByMasina(masina: string): ComandaRow[] {
  return comenziWindowSegment().filter((c) => c.masina === masina);
}
export function comenziByLuna(luna_key: string): ComandaRow[] {
  const f = getFilters();
  return getComenzi().filter(
    (c) => c.luna_key === luna_key && (f.segment === "all" || c.segment === f.segment),
  );
}
